import React from 'react';

export interface HeaderSlotContextValue {
  headerSlot: React.ReactNode;
  ignoreHeaderPadding: boolean;
  showSidebar: boolean;
  setHeader: (content: React.ReactNode) => void;
  clearHeader: () => void;
  setHeaderPadding: (ignore: boolean) => void;
  setShowSidebar: (show: boolean) => void;
}

export const HeaderSlotContext =
  React.createContext<HeaderSlotContextValue | null>(null);

export const HeaderSlotProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [headerSlot, setHeaderSlot] =
    React.useState<React.ReactNode>(undefined);
  const [ignoreHeaderPadding, setIgnoreHeaderPadding] = React.useState(false);
  const [showSidebar, setShowSidebarState] = React.useState(true);

  const setHeader = React.useCallback((content: React.ReactNode) => {
    setHeaderSlot(content);
  }, []);

  const clearHeader = React.useCallback(() => {
    setHeaderSlot(undefined);
  }, []);

  const setHeaderPadding = React.useCallback((ignore: boolean) => {
    setIgnoreHeaderPadding(ignore);
  }, []);

  const setShowSidebar = React.useCallback((show: boolean) => {
    setShowSidebarState(show);
  }, []);

  const value = React.useMemo<HeaderSlotContextValue>(
    () => ({
      headerSlot,
      ignoreHeaderPadding,
      showSidebar,
      setHeader,
      clearHeader,
      setHeaderPadding,
      setShowSidebar,
    }),
    [
      headerSlot,
      ignoreHeaderPadding,
      showSidebar,
      setHeader,
      clearHeader,
      setHeaderPadding,
      setShowSidebar,
    ],
  );

  return (
    <HeaderSlotContext.Provider value={value}>
      {children}
    </HeaderSlotContext.Provider>
  );
};

export function useHeaderSlot(): HeaderSlotContextValue {
  const context = React.useContext(HeaderSlotContext);
  if (context === null) {
    throw new Error('useHeaderSlot must be used within a HeaderSlotProvider');
  }
  return context;
}
